/**
 * AuthForgotPassword — Shared forgot-password page component.
 *
 * Identical layout across all apps. Visual differences are controlled
 * entirely through the AuthBrandConfig prop.
 */

import React, { useState } from 'react';
import LiquidGlass from 'liquid-glass-react';
import { AuthSpinner } from './AuthSpinner';
import type { AuthForgotPasswordProps } from './types';

const AuthForgotPassword: React.FC<AuthForgotPasswordProps> = ({
  brand,
  onSubmit,
  onNavigateToSignIn,
  onNavigateToSignUp,
}) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const gradient = `linear-gradient(180deg, ${brand.gradientFrom} 0%, ${brand.gradientTo} 100%)`;
  const layout = brand.layout ?? {};
  const logoBottomSpacing = layout.logoBottomSpacing ?? 32;
  const titleFontSize = layout.titleFontSize ?? 40;
  const subtitleBottomSpacing = layout.subtitleBottomSpacing ?? 48;
  const cardWidth = layout.cardWidth ?? 331;
  const cardMinHeight = layout.cardMinHeight ?? 260;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await onSubmit(email);
      setIsSubmitted(true);
    } catch (err: any) {
      console.error('Password reset error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Success state
  if (isSubmitted) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center px-6"
        style={{ background: gradient }}
      >
        <div className="flex flex-col items-center max-w-md w-full">
          {/* Logo */}
          <div style={{ marginBottom: `${logoBottomSpacing}px` }}>{brand.logo}</div>

          {/* Title */}
          <h1
            className="mb-2 text-center"
            style={{
              fontFamily: brand.titleFont,
              fontWeight: 400,
              color: brand.titleColor,
              lineHeight: 1.1,
              letterSpacing: '-0.8px',
              fontSize: `${titleFontSize}px`,
            }}
          >
            {brand.forgotPassword.successTitle}
          </h1>

          {/* Subtitle */}
          <p
            className="text-[13px] tracking-[0.08em] uppercase text-center"
            style={{
              color: brand.subtitleColor,
              fontFamily: brand.bodyFont,
              fontWeight: 600,
              marginBottom: `${Math.max(24, subtitleBottomSpacing - 16)}px`,
            }}
          >
            Reset link sent to {email}
          </p>

          {/* Simple glass container for success (LiquidGlass is oversized for small content) */}
          <div
            className="flex flex-col items-center gap-3 w-full"
            style={{
              maxWidth: `${cardWidth}px`,
              padding: '20px 16px',
              borderRadius: '24px',
              background: 'rgba(255, 255, 255, 0.35)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.5)',
            }}
          >
            <button
              onClick={onNavigateToSignIn}
              className="w-full h-11 flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
              style={{
                backgroundColor: brand.buttonBg,
                color: brand.buttonTextColor,
                borderRadius: '10px',
                maxWidth: '303px',
              }}
            >
              <span
                className="text-xs uppercase tracking-wider"
                style={{ fontFamily: brand.bodyFont, fontWeight: 600 }}
              >
                Return to Sign In
              </span>
            </button>
            <button
              onClick={() => setIsSubmitted(false)}
              className="w-full h-8 flex items-center justify-center transition-opacity hover:opacity-70"
              style={{
                backgroundColor: 'transparent',
                color: brand.linkColor,
                border: 'none',
                maxWidth: '303px',
              }}
            >
              <span
                className="text-xs uppercase tracking-wider"
                style={{ fontFamily: brand.bodyFont, fontWeight: 600 }}
              >
                Didn't receive it? Send again
              </span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Form state
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-6"
      style={{ background: gradient }}
    >
      <div className="flex flex-col items-center max-w-md w-full">
        {/* Logo */}
        <div style={{ marginBottom: `${logoBottomSpacing}px` }}>{brand.logo}</div>

        {/* Title */}
        <h1
          className="mb-2 text-center"
          style={{
            fontFamily: brand.titleFont,
            fontWeight: 400,
            color: brand.titleColor,
            lineHeight: 1.1,
            letterSpacing: '-0.8px',
            fontSize: `${titleFontSize}px`,
          }}
        >
          {brand.forgotPassword.title}
        </h1>

        {/* Subtitle */}
        <p
          className="text-[13px] tracking-[0.08em] uppercase"
          style={{
            color: brand.subtitleColor,
            fontFamily: brand.bodyFont,
            fontWeight: 600,
            marginBottom: `${subtitleBottomSpacing}px`,
          }}
        >
          {brand.forgotPassword.subtitle}
        </p>

        {/* Liquid Glass container */}
        <div
          className="relative w-full"
          style={{
            maxWidth: `${cardWidth}px`,
            minHeight: `${cardMinHeight}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LiquidGlass
            overLight={false}
            cornerRadius={24}
            padding="16px"
            blurAmount={0.08}
            saturation={100}
            displacementScale={18}
            aberrationIntensity={0.5}
            mode="standard"
            elasticity={0.12}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
            }}
          >
            <div className="flex flex-col items-center gap-[15px]" style={{ minWidth: '280px' }}>
              <form onSubmit={handleSubmit} className="w-full flex flex-col gap-3" style={{ maxWidth: '303px' }}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="Email"
                  className="w-full h-11 px-4 rounded-xl text-sm outline-none transition-all focus:ring-2 focus:ring-black/10"
                  style={{
                    backgroundColor: 'transparent',
                    color: brand.titleColor,
                    border: '1px solid rgba(0, 0, 0, 0.15)',
                    fontFamily: brand.bodyFont,
                  }}
                />
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-11 flex items-center justify-center gap-2 transition-opacity hover:opacity-90 disabled:opacity-50"
                  style={{
                    backgroundColor: brand.buttonBg,
                    color: brand.buttonTextColor,
                    borderRadius: '10px',
                  }}
                >
                  {isLoading ? (
                    <>{brand.spinnerSm || <AuthSpinner brand={brand} size="sm" />}</>
                  ) : (
                    <span
                      className="text-xs uppercase tracking-wider"
                      style={{ fontFamily: brand.bodyFont, fontWeight: 600 }}
                    >
                      Send Reset Link
                    </span>
                  )}
                </button>
              </form>

              <button
                onClick={onNavigateToSignIn}
                className="w-full h-8 flex items-center justify-center transition-opacity hover:opacity-70"
                style={{
                  backgroundColor: 'transparent',
                  color: brand.linkColor,
                  border: 'none',
                  maxWidth: '303px',
                }}
              >
                <span
                  className="text-xs uppercase tracking-wider"
                  style={{ fontFamily: brand.bodyFont, fontWeight: 600 }}
                >
                  Back to Sign In
                </span>
              </button>

              {onNavigateToSignUp && (
                <button
                  onClick={onNavigateToSignUp}
                  className="w-full h-8 flex items-center justify-center transition-opacity hover:opacity-70"
                  style={{
                    backgroundColor: 'transparent',
                    color: brand.linkColor,
                    border: 'none',
                    maxWidth: '303px',
                  }}
                >
                  <span
                    className="text-xs uppercase tracking-wider"
                    style={{ fontFamily: brand.bodyFont, fontWeight: 600 }}
                  >
                    New User? Sign Up
                  </span>
                </button>
              )}
            </div>
          </LiquidGlass>
        </div>
      </div>
    </div>
  );
};

export { AuthForgotPassword };
